<template>
  <div class="split-helpers">
    <v-dialog
      v-if="term"
      v-model="generateSplitDialog"
      max-width="480"
      persistent
    >
      <v-card class="confirm-dialog-card">
        <div class="confirm-dialog-header">
          <v-icon color="success" size="32">mdi-shuffle-variant</v-icon>
          <span class="confirm-dialog-title"
            >Generate New Split for term {{ term }}</span
          >
        </div>
        <v-card-text class="confirm-dialog-body">
          <v-form ref="splitForm" lazy-validation>
            <v-text-field
              v-model="newSetSplitName"
              label="Split Name"
              :rules="splitNameRules"
              :error-messages="
                isDuplicateSplitName
                  ? [
                      'A split with this name already exists for this term.',
                    ]
                  : []
              "
              required
            />
            <v-text-field
              v-model="numClasses"
              label="Number of classes"
              type="number"
              min="1"
              :rules="[rules.requiredNumber]"
              required
            />
            <strong v-if="studentCount"
              >Total Students to split: {{ studentCount }}</strong
            >
            <br v-if="studentCount && numClasses" />
            <strong v-if="studentCount && numClasses"
              >Class Size Average:
              {{ Math.round(studentCount / numClasses) }}</strong
            >
          </v-form>
        </v-card-text>
        <v-card-actions class="confirm-dialog-actions">
          <v-btn :disabled="loading" flat @click="generateSplitDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            color="success"
            :loading="loading"
            :disabled="loading || !canGenerate"
            @click="doGenerateSplit"
          >
            <v-icon left small>mdi-shuffle-variant</v-icon>
            Generate
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteSplitDialog" max-width="480" persistent>
      <v-card class="confirm-dialog-card">
        <div class="confirm-dialog-header">
          <v-icon color="error" size="32">mdi-delete-alert</v-icon>
          <span class="confirm-dialog-title"
            >Delete Split {{ setSplitName }}</span
          >
        </div>
        <v-card-text class="confirm-dialog-body">
          Are you sure you want to delete split
          <strong>{{ setSplitName }}</strong>
          for term <strong>{{ term }}</strong>? This will
          <strong>permanently delete</strong> all class assignments for this
          split.
        </v-card-text>
        <v-card-actions class="confirm-dialog-actions">
          <v-btn
            flat
            :disabled="deleteLoading"
            @click="deleteSplitDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            color="error"
            :loading="deleteLoading"
            @click="deleteCurrentSplit"
          >
            <v-icon left small>mdi-delete</v-icon>
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="unassignedDialog" max-width="560">
      <v-card class="confirm-dialog-card">
        <div class="confirm-dialog-header">
          <v-icon color="warning" size="32">mdi-account-off</v-icon>
          <span class="confirm-dialog-title"
            >Unenrolled Students ({{ unassignedStudents.length }})</span
          >
        </div>
        <v-card-text class="confirm-dialog-body unassigned-dialog-body">
          <div v-if="!unassignedStudents.length" class="unassigned-empty">
            All students with flags are enrolled in a class.
          </div>
          <v-list v-else dense class="unassigned-list">
            <v-list-tile
              style="border-bottom: 2px dashed #e0e0e0;"
              v-for="student in unassignedStudents"
              :key="student.studentId"
            >
              <v-list-tile-content>
                <v-list-tile-title>{{ student.displayName }}</v-list-tile-title>
                <v-list-tile-sub-title>
                  ID: {{ student.studentId
                  }}<span v-if="student.engName"> · {{ student.engName }}</span>
                </v-list-tile-sub-title>
              </v-list-tile-content>
              <v-list-tile-action>
                <v-icon
                  title="Edit"
                  small
                  color="success"
                  @click="editStudent(student.studentId)"
                  >mdi-pencil</v-icon
                >
              </v-list-tile-action>
            </v-list-tile>
          </v-list>
        </v-card-text>
        <v-card-actions class="confirm-dialog-actions">
          <v-btn flat @click="unassignedDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-btn
      :disabled="!term || loading || studentCount <= 0"
      color="success"
      round
      @click="openGenerateDialog"
    >
      <v-icon left small>mdi-shuffle-variant</v-icon>
      Generate New Split
    </v-btn>
    <v-btn
      v-if="hasExistingSplit && setSplitName"
      color="error"
      round
      :loading="deleteLoading"
      :disabled="!term || !setSplitName || deleteLoading || loading"
      @click="deleteSplitDialog = true"
    >
      <v-icon left small>mdi-delete</v-icon>
      Delete current Split
    </v-btn>
    <v-btn
      v-if="unassignedStudents.length && setSplitName"
      color="warning"
      round
      :disabled="!term"
      @click="unassignedDialog = true"
    >
      <v-icon left small>mdi-account-off</v-icon>
      Unenrolled Students ({{ unassignedStudents.length }})
    </v-btn>
  </div>
</template>

<script>
export default {
  name: "SchoolSplitHelpers",
  props: {
    term: {
      type: String,
      default: "",
    },
    setSplitName: {
      type: String,
      default: "",
    },
    setSplitNames: {
      type: Array,
      default: () => [],
    },
    studentCount: {
      type: Number,
      default: 0,
    },
    hasExistingSplit: {
      type: Boolean,
      default: false,
    },
    allStudents: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      generateSplitDialog: false,
      deleteSplitDialog: false,
      unassignedDialog: false,
      numClasses: null,
      newSetSplitName: "",
      loading: false,
      deleteLoading: false,
      rules: {
        requiredNumber: (v) => (!!v && parseInt(v, 10) >= 1) || "Required",
      },
    };
  },
  computed: {
    isDuplicateSplitName() {
      const name = String(this.newSetSplitName || "")
        .trim()
        .toLowerCase();
      if (!name) return false;
      return (this.setSplitNames || []).some(
        (n) =>
          String(n || "")
            .trim()
            .toLowerCase() === name
      );
    },
    splitNameRules() {
      return [
        (v) => !!String(v || "").trim() || "Required",
        (v) => {
          const name = String(v || "")
            .trim()
            .toLowerCase();
          if (!name) return true;
          const exists = (this.setSplitNames || []).some(
            (n) =>
              String(n || "")
                .trim()
                .toLowerCase() === name
          );
          return (
            !exists || "A split with this name already exists for this term."
          );
        },
      ];
    },
    canGenerate() {
      const name = String(this.newSetSplitName || "").trim();
      const numClasses = parseInt(this.numClasses, 10);
      return (
        !!name &&
        !this.isDuplicateSplitName &&
        !!numClasses &&
        numClasses >= 1
      );
    },
    unassignedStudents() {
      return (this.allStudents || [])
        .filter((student) => {
          const hasFlags = (student.flags || []).some(
            (flag) => !flag.isDeleted && String(flag.flag || "").trim()
          );
          if (!hasFlags) return false;
          const enrolled = (student.enrolledToClasses || []).filter(
            (row) => !row.isDeleted && row.classNumber != null
          );
          return enrolled.length === 0;
        })
        .map((student) => {
          const studentId = String(student.studentId || "").trim();
          const hebName = (
            String(student.hebFirstName || "").trim() +
            " " +
            String(student.hebLastName || "").trim()
          ).trim();
          const engName = (
            String(student.firstName || "").trim() +
            " " +
            String(student.lastName || "").trim()
          ).trim();
          return {
            studentId: student.studentId,
            displayName: hebName || engName || studentId,
            engName: engName,
          };
        })
        .sort((a, b) =>
          String(a.displayName).localeCompare(String(b.displayName))
        );
    },
  },
  methods: {
    editStudent(studentId) {
      this.$emit("editStudent", studentId);
    },
    openGenerateDialog() {
      this.numClasses = null;
      this.newSetSplitName = "";
      this.generateSplitDialog = true;
      if (this.$refs.splitForm) this.$refs.splitForm.resetValidation();
    },
    async doGenerateSplit() {
      if (!this.$refs.splitForm.validate() || !this.canGenerate) return;
      const term = String(this.term || "").trim();
      const setSplitName = String(this.newSetSplitName || "").trim();
      if (!term || !setSplitName || this.isDuplicateSplitName) return;
      const numClasses = parseInt(this.numClasses, 10);
      this.loading = true;
      try {
        await this.$http.post(
          `api/SchoolSplitStudentClasses/GenerateSplit?term=${encodeURIComponent(
            term
          )}&numClasses=${numClasses}&setSplitName=${encodeURIComponent(
            setSplitName
          )}`
        );
        this.generateSplitDialog = false;
        this.$emit("generated", setSplitName);
        this.notifySuccess("Split generated successfully.");
      } catch (error) {
        // Error toast is handled by the axios interceptor.
      } finally {
        this.loading = false;
      }
    },
    async deleteCurrentSplit() {
      const term = String(this.term || "").trim();
      const setSplitName = String(this.setSplitName || "").trim();
      if (!term || !setSplitName) return;
      this.deleteLoading = true;
      try {
        await this.$http.put(
          `api/SchoolSplitStudentClasses/DeleteSplit?term=${encodeURIComponent(
            term
          )}&setSplitName=${encodeURIComponent(setSplitName)}`
        );
        this.deleteSplitDialog = false;
        this.notifySuccess("Delete successfully.");
        this.$emit("refresh");
      } catch (error) {
        this.notifyError("Failed to Delete.");
      } finally {
        this.deleteLoading = false;
      }
    },
    notifySuccess(text) {
      this.$notify({ text: text, group: "notification", type: "success" });
    },
    notifyError(text) {
      this.$notify({ text: text, group: "notification", type: "error" });
    },
  },
};
</script>

<style scoped>
.split-helpers {
  display: flex;
  align-items: center;
  gap: 12px;
}

.confirm-dialog-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.confirm-dialog-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px 8px;
}

.confirm-dialog-title {
  font-size: 18px;
  font-weight: 700;
  color: #37474f;
}

.confirm-dialog-body {
  padding: 8px 24px 16px !important;
  font-size: 14px;
  color: #546e7a;
  line-height: 1.6;
}

.confirm-dialog-actions {
  padding: 8px 16px 16px !important;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.unassigned-dialog-body {
  max-height: 420px;
  overflow-y: auto;
}

.unassigned-empty {
  padding: 8px 0;
}

.unassigned-list {
  padding: 0;
}

.unassigned-list >>> .v-list__tile {
  border-bottom: 1px solid #e0e0e0;
}

.unassigned-list >>> .v-list__tile:last-child {
  border-bottom: none;
}
</style>
